import { ArrowLeft } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useIsDesktopViewport } from '../../../hooks/useIsDesktopViewport';
import { emailForgotPassword, getAuthError, getUserLoadingState, setAuthFlow } from '../../../store/slices/authSlice';
import CustomButton, { BUTTON_STYLE_TYPE } from '../../common/Button';
import styles from './styles.module.scss';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPasswordStep = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isDesktop = useIsDesktopViewport();
    const loading = useSelector(getUserLoadingState);
    const error = useSelector(getAuthError);
    const {
        register,
        handleSubmit,
        trigger,
        formState: { errors, isValid, touchedFields }
    } = useForm({ mode: 'onChange' });

    // See SetPasswordStep's identical note — isValid otherwise reports
    // true on mount before the field has been validated.
    useEffect(() => {
        trigger();
    }, [trigger]);

    const onSubmit = ({ email }) => {
        if (loading) {
            return;
        }
        dispatch(emailForgotPassword(email.trim().toLowerCase()));
    };

    // HORO-10509: same Roboto 400/16px desktop-card override as Sign
    // in/Set password's field labels — confirmed via Figma inspector on
    // this exact screen's "Email address" label (node 4542:50427).
    const fieldLabelClassName = isDesktop ? `${styles.fieldLabel} ${styles.fieldLabelDesktop}` : styles.fieldLabel;

    return (
        <>
            {/* HORO-10509: now gets the same desktop-card/full-screen
                treatment as Sign in/Set password (see AuthModal.js), so it
                switched from a plain "← Back" text link to the shared
                icon-button header row those screens use — confirmed via
                Figma, both breakpoints share this row (arrow + centered
                title), not a stacked back-link-then-title layout. */}
            <div className={styles.signInHeader}>
                <button
                    type="button"
                    className={styles.backIconBtn}
                    aria-label={t('buttons.back')}
                    onClick={() => dispatch(setAuthFlow('sign-in'))}
                >
                    <ArrowLeft size={24} color="#FFFFFF" />
                </button>
                <h2 className={`${styles.title} ${styles.titleMixedCase}`}>{t('modal.forgotPasswordTitle')}</h2>
                <div className={styles.headerSpacer} aria-hidden="true" />
            </div>
            {/* Same subtitle on both breakpoints — confirmed via Figma,
                unlike Sign in/Set password where it's desktop-only. */}
            <p className={styles.subtitle}>{t('modal.forgotPasswordLabel')}</p>
            <form onSubmit={handleSubmit(onSubmit)}>
                <span className={fieldLabelClassName}>{t('modal.emailFieldLabel')}</span>
                <div className={styles.inputWrapper}>
                    <input
                        className={styles.fieldInput}
                        type="email"
                        autoComplete="email"
                        placeholder={t('modal.emailPlaceholder')}
                        {...register('email', { required: true, pattern: EMAIL_REGEX })}
                    />
                    {errors.email && touchedFields.email ? (
                        <span className={styles.error}>{t('errors.invalidEmail', 'Enter a valid email address')}</span>
                    ) : (
                        <span className={styles.fieldHelperText}>{t('modal.forgotPasswordHelperText')}</span>
                    )}
                </div>
                {error && <p className={styles.formError}>{error.message}</p>}
                <div className={styles.submitBtnWrap}>
                    <CustomButton
                        text={t('buttons.continue')}
                        loading={loading}
                        disable={loading || !isValid}
                        styleType={BUTTON_STYLE_TYPE.PURPLE}
                    />
                </div>
            </form>
        </>
    );
};

export default ForgotPasswordStep;
